import type { Sound } from "../../runtime";
import {
  FX_appear,
  FX_axeCharge,
  FX_axeShockwave,
  FX_cleanserLoop,
  FX_damage,
  FX_dash,
  FX_death,
  FX_doors,
  FX_effect1,
  FX_effect2,
  FX_effect3,
  FX_enemyShot1,
  FX_enemyShot2,
  FX_enemyShot3,
  FX_enemySlash,
  FX_explode1,
  FX_explode2,
  FX_explode3,
  FX_explode4,
  FX_explode5,
  FX_explode6,
  FX_hit1,
  FX_hit2,
  FX_hit3,
  FX_hitSpecial1,
  FX_hitSpecial2,
  FX_hitSpecial3,
  FX_item1,
  FX_item2,
  FX_item3,
  FX_menuChange1,
  FX_menuChange2,
  FX_menuSelect1,
  FX_mine1,
  FX_mine2,
  FX_missileLaunch,
  FX_missionBar1,
  FX_missionBar2,
  FX_openingBoost,
  FX_openingComputer,
  FX_scytheBlackHole,
  FX_scytheShot,
  FX_shine,
  FX_staffCatch,
  FX_staffSpin,
  FX_staffThrow,
  FX_swipe1,
  FX_swipe2,
  FX_swipe3,
  FX_swipeB1,
  FX_swipeB2,
  FX_swipeB3,
  FX_swipeC1,
  FX_swipeC2,
  FX_swipeC3,
  FX_swipeD1,
  FX_swipeD2,
  FX_swipeD3,
  FX_swipeE1,
  FX_swipeE2,
  FX_swipeE3,
  FX_swordSpin,
  FX_textBoxAppear,
  FX_thunder,
  FX_weaponChange,
  MUS_boss,
  MUS_bossIntro,
  MUS_ending,
  MUS_level01,
  MUS_level02,
  MUS_level03,
  MUS_level04,
  MUS_level05,
  MUS_level06,
  MUS_levelSelect,
  MUS_opening,
} from "../assets";

/** Constructor of a generated sound asset class. */
export type SoundClass = new () => Sound;

/**
 * Sound effects keyed by the `SoundFX` method that plays them. Every entry is
 * instantiated once and reused. `cleanserLoop` is the only looping entry; the
 * rest are fired with a bare `play()`.
 */
export const SOUND_EFFECTS = {
  menuChange1: FX_menuChange1,
  menuChange2: FX_menuChange2,
  menuSelect1: FX_menuSelect1,
  death: FX_death,
  doors: FX_doors,
  shine: FX_shine,
  effect1: FX_effect1,
  effect2: FX_effect2,
  effect3: FX_effect3,
  item1: FX_item1,
  item2: FX_item2,
  item3: FX_item3,
  openingComputer: FX_openingComputer,
  openingBoost: FX_openingBoost,
  textBoxAppear: FX_textBoxAppear,
  appear: FX_appear,
  thunder: FX_thunder,
  swordSpin: FX_swordSpin,
  explodeBig: FX_explode4,
  explodeHuge: FX_explode5,
  explodeGargantuan: FX_explode6,
  weaponChange: FX_weaponChange,
  dash: FX_dash,
  missileLaunch: FX_missileLaunch,
  cleanserLoop: FX_cleanserLoop,
  missionBar1: FX_missionBar1,
  missionBar2: FX_missionBar2,
  enemyShotOrb: FX_enemyShot1,
  enemyShotSickle: FX_enemyShot2,
  enemyShotLaser: FX_enemyShot3,
  mine1: FX_mine1,
  mine2: FX_mine2,
  enemySlash: FX_enemySlash,
  damage: FX_damage,
  scytheBlackHole: FX_scytheBlackHole,
  scytheShot: FX_scytheShot,
  staffCatch: FX_staffCatch,
  staffSpin: FX_staffSpin,
  staffThrow: FX_staffThrow,
  axeCharge: FX_axeCharge,
  axeShockwave: FX_axeShockwave,
} as const satisfies Record<string, SoundClass>;

export type SoundEffectName = keyof typeof SOUND_EFFECTS;

/**
 * Effects that pick one of three variants per play. The picker is a single
 * `Math.ceil(Math.random() * 3)`, so results 1/2/3 map to index 0/1/2.
 */
export const SOUND_VARIANTS = {
  hitEnemy: [FX_hit1, FX_hit2, FX_hit3],
  hitEnemySpecial: [FX_hitSpecial1, FX_hitSpecial2, FX_hitSpecial3],
  explodeEnemy: [FX_explode1, FX_explode2, FX_explode3],
  swipe: [FX_swipe1, FX_swipe2, FX_swipe3],
  swipeB: [FX_swipeB1, FX_swipeB2, FX_swipeB3],
  swipeC: [FX_swipeC1, FX_swipeC2, FX_swipeC3],
  swipeD: [FX_swipeD1, FX_swipeD2, FX_swipeD3],
  swipeE: [FX_swipeE1, FX_swipeE2, FX_swipeE3],
} as const satisfies Record<
  string,
  readonly [SoundClass, SoundClass, SoundClass]
>;

export type SoundVariantName = keyof typeof SOUND_VARIANTS;

/**
 * Music tracks. Unlike effects these are constructed fresh on every play.
 * `opening` is the only track that is not looped.
 */
export const MUSIC = {
  level01: { sound: MUS_level01, loops: 99999 },
  level02: { sound: MUS_level02, loops: 99999 },
  level03: { sound: MUS_level03, loops: 99999 },
  level04: { sound: MUS_level04, loops: 99999 },
  level05: { sound: MUS_level05, loops: 99999 },
  level06: { sound: MUS_level06, loops: 99999 },
  boss: { sound: MUS_boss, loops: 99999 },
  bossIntro: { sound: MUS_bossIntro, loops: 99999 },
  levelSelect: { sound: MUS_levelSelect, loops: 99999 },
  ending: { sound: MUS_ending, loops: 99999 },
  opening: { sound: MUS_opening, loops: 0 },
} as const satisfies Record<
  string,
  { readonly sound: SoundClass; readonly loops: number }
>;

export type MusicName = keyof typeof MUSIC;

/** `musicStart` command number -> track. Unlisted numbers start no track. */
export const MUSIC_TRACKS: Readonly<Record<number, MusicName | undefined>> = {
  1: "level01",
  2: "level02",
  3: "level03",
  4: "level04",
  5: "level05",
  6: "level06",
  9: "bossIntro",
  10: "boss",
  15: "opening",
  16: "ending",
  20: "levelSelect",
};

/** `updateSoundState` argument -> `[soundOn, musOn]`. Other values change nothing. */
export const SOUND_STATES: Readonly<
  Record<number, readonly [boolean, boolean] | undefined>
> = {
  1: [true, true],
  2: [true, false],
  3: [false, true],
  4: [false, false],
};
